import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DetailHero } from '@/components/common/DetailHero';
import { ErrorState } from '@/components/common/ErrorState';
import { FormDialogHeader } from '@/components/common/FormDialog';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { formatDateRangeWithYear } from '@/utils/date';
import { pluralize } from '@/utils/number';
import { leaveReportDatesQueryOptions } from '../api/leaveReport.api';
import { LEAVE_REPORT_DATE_CATEGORY } from '../constants/leaveReport.constants';
import type { LeaveReportDateSelection, LeaveReportPeriod } from '../types/leaveReport.types';
import { getPeriodCalendarMonths, type CalendarMonth } from '../utils/leaveReportCalendar';
import { getDateCategoryCount, getEmployeeName } from './LeaveReportColumns';
import { LeaveReportDatesCalendar } from './LeaveReportDatesCalendar';

const DAY_UNIT = 'day';
const COUNT_LABEL = 'Total';
const SINGLE_MONTH_DIALOG_CLASS = 'sm:max-w-md';
const SIDE_BY_SIDE_DIALOG_CLASS = 'sm:max-w-3xl';

interface LeaveReportDatesDialogProps {
  selection: LeaveReportDateSelection | null;
  period: LeaveReportPeriod;
  onClose: () => void;
}

export function LeaveReportDatesDialog({
  selection,
  period,
  onClose,
}: LeaveReportDatesDialogProps) {
  const months = useMemo(() => getPeriodCalendarMonths(period), [period]);
  const isSideBySide = months.length > 1;

  return (
    <Dialog open={selection !== null} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        className={isSideBySide ? SIDE_BY_SIDE_DIALOG_CLASS : SINGLE_MONTH_DIALOG_CLASS}
      >
        {selection ? (
          <ReportDatesContent
            selection={selection}
            period={period}
            months={months}
            isSideBySide={isSideBySide}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

interface ReportDatesContentProps {
  selection: LeaveReportDateSelection;
  period: LeaveReportPeriod;
  months: CalendarMonth[];
  isSideBySide: boolean;
}

function ReportDatesContent({ selection, period, months, isSideBySide }: ReportDatesContentProps) {
  const { employee, category } = selection;
  const { label, icon } = LEAVE_REPORT_DATE_CATEGORY[category];
  const { data, error, refetch, isRefetching } = useQuery(
    leaveReportDatesQueryOptions(employee.userId, period),
  );

  const dayCount = getDateCategoryCount(employee, category);

  return (
    <>
      <FormDialogHeader
        icon={icon}
        title={label}
        description={formatDateRangeWithYear(period.dateFrom, period.dateTo)}
      />
      <DetailHero
        heading={
          <span className="text-base font-semibold tracking-tight">
            {getEmployeeName(employee)}
          </span>
        }
        subline={
          employee.hrEmpId ? (
            <span className="text-sm text-muted-foreground tabular-nums">
              <span className="font-semibold text-foreground">ID:</span> {employee.hrEmpId}
            </span>
          ) : null
        }
        amountLabel={COUNT_LABEL}
        amount={dayCount}
        amountUnit={pluralize(dayCount, DAY_UNIT)}
        amountNote={null}
      />
      {error ? (
        <ErrorState
          error={error}
          fallbackTitle="Could not load the dates"
          onRetry={() => void refetch()}
          isRetrying={isRefetching}
          size="compact"
        />
      ) : (
        <LeaveReportDatesCalendar
          dates={data}
          category={category}
          months={months}
          isSideBySide={isSideBySide}
        />
      )}
    </>
  );
}
